import { AppConfig } from "./config/schemas.js";
import { logger } from "./logger.js";
import { z } from "zod";
import { appConfig as prod } from "../config/config-prod.js";

// NodeEnv is a commonly used env var BUT using it here can result in conflicts with
// a lot of other libraries that use it.
// TODO: Rename to AppEnv
export type AppEnv = "test" | "development" | "production";
export type DevMode = "linked" | "source";

export const appEnv = z.object({
  APP_ENV: z
    .any()
    .transform((val) => {
      return val?.toString().trim().toLowerCase();
    })
    .refine((val) => ["test", "development", "production"].includes(val), {
      message: "APP_ENV must be one of: test, development, production",
    })
    .default("production"),
  DEV_MODE: z
    .string()
    .transform((val) => val?.trim().toLowerCase())
    .pipe(z.enum(["linked", "source"]))
    .optional(),
  ONEGREP_APP_CONFIG: z.string().optional(),
});

export function getEnv(): z.infer<typeof appEnv> {
  const parsedEnv = appEnv.strip().parse(process.env);

  if (["development"].includes(parsedEnv.APP_ENV) && !parsedEnv.DEV_MODE) {
    throw new Error(`DEV_MODE is required when APP_ENV is development`);
  }
  if (parsedEnv.APP_ENV === "production" && !!parsedEnv.DEV_MODE) {
    throw new Error(`DEV_MODE is not allowed when APP_ENV is production`);
  }
  return parsedEnv;
}

export const getAppConfig = async (): Promise<AppConfig> => {
  const env = getEnv();
  logger.debug(`App config loaded for ${env.APP_ENV} environment`);

  // If we're in production, we use bundled prod config
  if (env.APP_ENV === "production") {
    return prod;
  }

  // If we're not in production, we dynamically import app config from a JSON file
  if (!env.ONEGREP_APP_CONFIG) {
    throw new Error("ONEGREP_APP_CONFIG is required if not in production");
  }

  const fs = await import("fs");

  // ? config-{var}.ts under the ../config/ directory
  const path = await import("path");
  const __dirname = path.dirname(new URL(import.meta.url).pathname);
  const configDir = path.resolve(__dirname, "..", "config");

  const appConfigFileName = `${env.ONEGREP_APP_CONFIG}.json`;
  const appConfigPath = path.resolve(configDir, appConfigFileName);
  logger.debug(`Loading app config from ${appConfigPath}`);

  const appConfig = JSON.parse(fs.readFileSync(appConfigPath, "utf8"));
  return appConfig;
};
